import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import { lazy, Suspense, useId, useState, type ComponentProps, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../buttons/AppButton";
import LoadingIndicator from "../../feedback/LoadingIndicator";
import ModalShell from "../../overlays/ModalShell";
import { classNames } from "../../styles/classNames";
import CopyButton from "../CopyButton";
import type { SourceLanguage } from "./sourceLanguage";

// Monaco is large; it loads only when a full view or comparison is shown.
const MonacoSource = lazy(() => import("./monaco/MonacoSource"));

interface SourceViewProps {
  /** Names the text for screen readers and titles its full view. */
  label: string;
  language: SourceLanguage;
  value: string;
  /**
   * `inline`: a light, bounded text block for snippets in lists and logs, with copy and a
   * button that opens the full view. `full`: the editor itself, with line numbers, find
   * and folding.
   */
  presentation?: "inline" | "full";
  /** Makes a full view editable; the caller keeps the draft and its checks. */
  onChange?: (value: string) => void;
  /**
   * An editable full view's validation: why the text as it stands is invalid. It shows under
   * the editor, is announced, and marks the editor invalid and described by it.
   */
  error?: string;
  /** The tallest a full view grows, in pixels; inline blocks stop at 16rem. */
  maxHeight?: number;
  className?: string;
}

/**
 * Source text (plain text, JSON, Turtle or Markdown source) shown or edited the same way
 * everywhere. Rendered prose stays MarkdownRenderer; this is for text read as written.
 */
export default function SourceView({ label, language, value, presentation = "inline", onChange, error, maxHeight = 560, className }: SourceViewProps) {
  const { t } = useTranslation();
  const [fullOpen, setFullOpen] = useState(false);
  const errorId = useId();
  if (presentation === "full") {
    return (
      <div className={classNames("space-y-1", className)}>
        <SourceToolbar value={value} />
        <FullSource kind="view" label={label} language={language} value={value} maxHeight={maxHeight} onChange={onChange} errorId={error === undefined ? undefined : errorId} />
        {error === undefined ? null : <p id={errorId} role="alert" className="app-error-text text-xs">{error}</p>}
      </div>
    );
  }
  return (
    <div className={classNames("space-y-1", className)}>
      <SourceToolbar value={value}>
        <AppButton size="xs" variant="ghost" onClick={() => { setFullOpen(true); }}>
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t("common.source.openFull")}
        </AppButton>
      </SourceToolbar>
      <pre className="app-code-panel app-source-scroll max-h-64 whitespace-pre-wrap break-words p-2 text-[11px]" tabIndex={0} aria-label={label}>
        {value}
      </pre>
      {fullOpen ? (
        <ModalShell title={label} onClose={() => { setFullOpen(false); }} size="6xl">
          <FullSource kind="view" label={label} language={language} value={value} maxHeight={640} />
        </ModalShell>
      ) : null}
    </div>
  );
}

/** Copy and any further actions, above a source block. */
export function SourceToolbar({ value, children }: { value: string; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {children}
      <CopyButton value={value} variant="ghost">{t("common.source.copy")}</CopyButton>
    </div>
  );
}

/** The lazily loaded editor, with a loading line in its place until it arrives. */
export function FullSource(props: ComponentProps<typeof MonacoSource>) {
  const { t } = useTranslation();
  return (
    <Suspense fallback={<LoadingIndicator label={t("common.source.loadingEditor")} className="py-6" />}>
      <MonacoSource {...props} />
    </Suspense>
  );
}
